// Convert uploaded image files to compressed base64 data URLs before sending
// them to the extraction endpoint. Mobile camera photos can be many MB; we
// downscale + re-encode as JPEG to keep request payloads small.

const MAX_DIMENSION = 2000; // px — long edge; plenty for card OCR
const JPEG_QUALITY = 0.85;

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });

/**
 * Read a file, downscale it so the longest edge is <= MAX_DIMENSION, and return
 * a JPEG data URL. Falls back to the raw data URL if canvas processing fails
 * (e.g. unsupported format) so extraction can still be attempted.
 */
export const fileToCompressedDataUrl = async (file: File): Promise<string> => {
  const dataUrl = await readAsDataUrl(file);
  try {
    const img = await loadImage(dataUrl);
    const longEdge = Math.max(img.width, img.height);
    const scale = longEdge > MAX_DIMENSION ? MAX_DIMENSION / longEdge : 1;
    const width = Math.round(img.width * scale);
    const height = Math.round(img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  } catch {
    return dataUrl;
  }
};

/** Process multiple files in parallel into compressed data URLs. */
export const filesToCompressedDataUrls = (files: File[]): Promise<string[]> =>
  Promise.all(files.map(fileToCompressedDataUrl));
